
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';

export default function ProductoCieloPage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user } = useAuth();
  const [hasPurchased, setHasPurchased] = useState(false);
  const [loading, setLoading] = useState(true);

  // Track scroll to add shadow/background
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when scrolling
  useEffect(() => {
    if (menuOpen) setMenuOpen(false);
  }, [scrolled]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [menuOpen]);

  // Check if the logged‑in user already purchased the product
  useEffect(() => {
    const checkPurchase = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('user_purchases')
          .select('*')
          .eq('user_id', user.id)
          .in('product_slug', ['producto-cielo', 'cielo', 'despierta-tu-voz'])
          .maybeSingle();

        if (error) {
          console.error('Supabase error while checking purchase:', error);
        } else if (data) {
          setHasPurchased(true);
        }
      } catch (e) {
        console.error('Unexpected error while checking purchase:', e);
      } finally {
        setLoading(false);
      }
    };

    checkPurchase();
  }, [user]);

  // Smooth scroll to the contact section
  const handlePurchase = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const contactSection = document.getElementById('contacto');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const navLinks = [
    { href: '/#rituales', label: 'Rituales gratuitos' },
    { href: '/#metodo', label: 'Un método propio' },
    { href: '/#espacio', label: 'Despierta tu voz' },
    { href: '/#sobre-mi', label: 'La voz detrás' },
    { href: '/#contacto', label: 'Contacto' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-md'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <Link
              to="/"
              className="flex items-center transform hover:scale-105 transition-transform duration-300"
            >
              <img
                src="https://static.readdy.ai/image/e4930f1328430401d33e8e8f8ac29155/d3c9f2d28e8d17a63b0e1d543ffef045.png"
                alt="Una voz más que propia"
                className="h-14 sm:h-16 w-auto"
              />
            </Link>

            {/* Desktop navigation */}
            <div className="hidden lg:flex items-center justify-center flex-1">
              <div className="flex items-center gap-8">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="text-sm font-medium transition-all duration-300 whitespace-nowrap hover:scale-110 cursor-pointer text-gray-700 hover:text-[#C4A5C7]"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            <div className="hidden lg:flex items-center">
              <Link
                to="/auth/login"
                className="px-5 py-2.5 bg-[#B394B6] text-white rounded-full text-sm font-semibold hover:bg-[#A384A6] transition-all duration-300 whitespace-nowrap shadow-md hover:shadow-lg flex items-center gap-2 transform hover:scale-105"
              >
                <i className="ri-user-line"></i>
                Mi espacio propio
              </Link>
            </div>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-300 text-gray-700 hover:bg-gray-100"
              aria-label="Abrir menú"
            >
              <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile overlay */}
        <div
          className={`lg:hidden fixed inset-0 top-20 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
            menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
          onClick={() => setMenuOpen(false)}
        />

        {/* Mobile menu */}
        <div
          className={`lg:hidden fixed top-20 left-0 right-0 bg-white shadow-xl transition-all duration-300 transform ${
            menuOpen ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0 pointer-events-none'
          }`}
        >
          <div className="px-6 py-6 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMenuOpen(false)}
                className="block px-4 py-3 text-gray-700 hover:text-[#C4A5C7] hover:bg-[#C4A5C7]/10 rounded-lg transition-all duration-200 font-medium"
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/auth/login"
              className="flex items-center justify-center gap-2 px-5 py-3 bg-[#B394B6] text-white rounded-full font-semibold hover:bg-[#A384A6] transition-all duration-300 shadow-md hover:shadow-lg mt-4"
            >
              <i className="ri-user-line"></i>
              Mi espacio propio
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-b from-[#F9F5FA] to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#C4A5C7]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#C4A5C7]/5 rounded-full blur-3xl"></div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Content */}
            <div>
              <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-[#C4A5C7]/10 rounded-full">
                <span className="w-2 h-2 bg-[#C4A5C7] rounded-full animate-pulse"></span>
                <span className="text-[#C4A5C7] text-sm font-semibold">
                  ETAPA 3 • RITUAL CIELO
                </span>
              </div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif text-gray-900 mb-6 leading-tight">
                Cielo
              </h1>

              <p className="text-xl md:text-2xl text-gray-700 mb-8 leading-relaxed">
                Amplía tu mirada y ve <strong>más allá de lo inmediato</strong>. Una experiencia de
                meditación guiada + escritura para expandir tu voz hacia nuevos horizontes creativos.
              </p>

              <div className="inline-block mb-10 px-6 py-3 bg-white rounded-2xl shadow-md border border-gray-100">
                <p className="text-lg text-gray-600 italic">
                  Deja de escribir lo que ya sabes.{' '}
                  <strong className="text-[#C4A5C7] not-italic">
                    Descubre lo que intuyes.
                  </strong>
                </p>
              </div>

              {/* Trust badges */}
              <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-[#C4A5C7] text-xl"></i>
                  <span>Acceso ilimitado</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-refresh-line text-[#C4A5C7] text-xl"></i>
                  <span>Repite cuando quieras</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-download-line text-[#C4A5C7] text-xl"></i>
                  <span>Materiales descargables</span>
                </div>
              </div>
            </div>

            {/* Right - Image */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#C4A5C7]/20 to-transparent rounded-3xl blur-2xl"></div>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/855ff5c529b0d3e0af33d0523f9377d5.jpg"
                  alt="Ritual Cielo"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included Section - 3 Phases Method */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-[#C4A5C7]/10 rounded-full">
              <i className="ri-award-line text-[#C4A5C7]"></i>
              <span className="text-[#C4A5C7] text-sm font-semibold">
                MÉTODO COMPLETO EN 3 FASES
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4">
              Un viaje hacia tu visión creativa
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Un <strong>sistema completo de 3 fases</strong> que conecta tus intuiciones más profundas
              con tu escritura. Desde la apertura de visión hasta textos que miran más allá.
            </p>
          </div>

          {/* Phase 1 */}
          <div className="mb-12">
            <div className="bg-gradient-to-br from-[#F9F5FA] to-white rounded-3xl border border-gray-100 overflow-hidden shadow-lg hover:shadow-xl transition-all">
              <div className="grid lg:grid-cols-2 gap-0">
                <div className="p-10 lg:p-12">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#C4A5C7] to-[#B394B6] rounded-2xl text-white font-bold text-xl shadow-lg">
                      1
                    </div>
                    <div>
                      <span className="text-xs text-[#C4A5C7] font-semibold uppercase tracking-wider">
                        Fase 1
                      </span>
                      <h3 className="text-2xl md:text-3xl font-serif text-gray-900">
                        Tomar conciencia
                      </h3>
                    </div>
                  </div>

                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    Salimos del ruido y entramos en <strong>perspectiva</strong>. Aquí es donde
                    empiezas a ver lo que normalmente no miras.
                  </p>

                  <div className="space-y-4">
                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#C4A5C7]/20 rounded-lg flex-shrink-0">
                        <i className="ri-bookmark-line text-[#C4A5C7] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">
                          Tarjetas de intención y elección
                        </h4>
                        <p className="text-sm text-gray-600">
                          Elige desde dónde vas a mirar: una pregunta, una preocupación, una intuición
                          o una imagen
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#C4A5C7]/20 rounded-lg flex-shrink-0">
                        <i className="ri-headphone-line text-[#C4A5C7] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">
                          Audio guiado — Apertura de visión
                        </h4>
                        <p className="text-sm text-gray-600">
                          Respirar, abrir atención y dejar que aparezca tu mirada sobre el mundo
                        </p>
                        <span className="inline-flex items-center gap-1 mt-2 text-xs text-[#C4A5C7] font-medium">
                          <i className="ri-music-2-line"></i> Frecuencia 432 Hz • Sentada/Relajación
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="relative h-64 lg:h-auto">
                  <img
                    src="https://readdy.ai/api/search-image?query=Ethereal%20dreamy%20woman%20with%20closed%20eyes%20looking%20upward%20in%20deep%20meditation%20surrounded%20by%20soft%20lavender%20light%20floating%20stars%20and%20cosmic%20imagery%20representing%20expanded%20vision%20and%20higher%20perspective%20warm%20muted%20purple%20tones%20artistic%20painterly%20style&width=600&height=700&seq=cielo-phase1-v3&orientation=portrait"
                    alt="Fase 1: Tomar conciencia expandida"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#F9F5FA] via-transparent to-transparent lg:from-transparent"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Phase 2 */}
          <div className="mb-12">
            <div className="bg-gradient-to-br from-[#F9F5FA] to-white rounded-3xl border border-gray-100 overflow-hidden shadow-lg hover:shadow-xl transition-all">
              <div className="grid lg:grid-cols-2 gap-0">
                <div className="relative h-64 lg:h-auto order-2 lg:order-1">
                  <img
                    src="https://readdy.ai/api/search-image?query=Artistic%20scene%20of%20woman%20creating%20vision%20map%20with%20cosmic%20elements%20stars%20questions%20and%20intuitions%20floating%20around%20her%20soft%20lavender%20lighting%20representing%20expanded%20creative%20vision%20and%20deep%20insight%20artistic%20documentary%20style&width=600&height=700&seq=cielo-phase2-v3&orientation=portrait"
                    alt="Fase 2: Mapa de visión"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-l from-[#F9F5FA] via-transparent to-transparent lg:from-transparent"></div>
                </div>

                <div className="p-10 lg:p-12 order-1 lg:order-2">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#C4A5C7] to-[#B394B6] rounded-2xl text-white font-bold text-xl shadow-lg">
                      2
                    </div>
                    <div>
                      <span className="text-xs text-[#C4A5C7] font-semibold uppercase tracking-wider">
                        Fase 2
                      </span>
                      <h3 className="text-2xl md:text-3xl font-serif text-gray-900">
                        Dejarse llevar
                      </h3>
                    </div>
                  </div>

                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    Sacamos del subconsciente tus <strong>temas de verdad</strong> —los que vuelven,
                    los que te tocan, los que te obsesionan— y les damos forma.
                  </p>

                  <div className="space-y-4">
                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#C4A5C7]/20 rounded-lg flex-shrink-0">
                        <i className="ri-compass-3-line text-[#C4A5C7] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Mapa de Visión</h4>
                        <p className="text-sm text-gray-600">
                          Preocupaciones reales, preguntas esenciales, intuiciones sobre el futuro,
                          imágenes que se repiten
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#C4A5C7]/20 rounded-lg flex-shrink-0">
                        <i className="ri-volume-up-line text-[#C4A5C7] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Mini audio guía</h4>
                        <p className="text-sm text-gray-600">
                          Te acompaña mientras completas el mapa, para no caer en el análisis infinito
                        </p>
                        <span className="inline-flex items-center gap-1 mt-2 text-xs text-[#C4A5C7] font-medium">
                          <i className="ri-file-download-line"></i> Descargable en PDF
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Phase 3 */}
          <div className="mb-12">
            <div className="bg-gradient-to-br from-[#F9F5FA] to-white rounded-3xl border border-gray-100 overflow-hidden shadow-lg hover:shadow-xl transition-all">
              <div className="grid lg:grid-cols-2 gap-0">
                <div className="p-10 lg:p-12">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#C4A5C7] to-[#B394B6] rounded-2xl text-white font-bold text-xl shadow-lg">
                      3
                    </div>
                    <div>
                      <span className="text-xs text-[#C4A5C7] font-semibold uppercase tracking-wider">
                        Fase 3
                      </span>
                      <h3 className="text-2xl md:text-3xl font-serif text-gray-900">
                        Integrarlo
                      </h3>
                    </div>
                  </div>

                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    Tu visión no se queda en una nube. La <strong>conviertes en un tema claro</strong>,
                    eliges un ángulo propio y lo dejas escrito para seguir desarrollándolo.
                  </p>

                  <div className="space-y-4">
                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#C4A5C7]/20 rounded-lg flex-shrink-0">
                        <i className="ri-book-3-line text-[#C4A5C7] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">
                          Diario de integración
                        </h4>
                        <p className="text-sm text-gray-600">
                          Convierte tu mapa en un tema claro y elige un ángulo propio
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#C4A5C7]/20 rounded-lg flex-shrink-0">
                        <i className="ri-edit-line text-[#C4A5C7] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">
                          Ejercicios de escritura visionaria
                        </h4>
                        <p className="text-sm text-gray-600">
                          Transforma visión en texto: imágenes, ritmo, metáforas, escenas y preguntas
                          que abren historia
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#C4A5C7]/20 rounded-lg flex-shrink-0">
                        <i className="ri-star-line text-[#C4A5C7] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Cierre de encarnación</h4>
                        <p className="text-sm text-gray-600">
                          Un gesto pequeño que confirme tu voz: una decisión creativa o una
                          frase‑regla que te sostenga
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="relative h-64 lg:h-auto">
                  <img
                    src="https://readdy.ai/api/search-image?query=Empowered%20woman%20writer%20at%20desk%20with%20completed%20vision%20journal%20pages%20floating%20around%20her%20soft%20lavender%20light%20emanating%20from%20above%20representing%20expanded%20creative%20vision%20and%20visionary%20writing%20warm%20muted%20purple%20tones%20artistic%20inspirational%20style&width=600&height=700&seq=cielo-phase3-v3&orientation=portrait"
                    alt="Fase 3: Integrar la visión"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#F9F5FA] via-transparent to-transparent lg:from-transparent"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary Value Box */}
          <div className="bg-gradient-to-r from-[#C4A5C7]/10 via-[#C4A5C7]/5 to-[#C4A5C7]/10 rounded-3xl p-8 md:p-10 border border-[#C4A5C7]/20">
            <div className="text-center mb-8">
              <h3 className="text-2xl md:text-3xl font-serif text-gray-900 mb-2">
                Todo esto incluido por solo <span className="text-[#C4A5C7]">64€</span>
              </h3>
              <p className="text-gray-600">Pago único • Acceso inmediato • Tuyo para siempre</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-xl p-5 shadow-sm border border-[#C4A5C7]/10">
                <div className="w-12 h-12 flex items-center justify-center bg-[#C4A5C7]/20 rounded-full mb-3">
                  <i className="ri-headphone-line text-[#C4A5C7] text-2xl"></i>
                </div>
                <p className="text-base font-semibold text-gray-900 mb-1">
                  Audio Apertura de Visión 432 Hz
                </p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Respirar, abrir atención y dejar que aparezca tu mirada sobre el mundo
                </p>
              </div>

              <div className="bg-white rounded-xl p-5 shadow-sm border border-[#C4A5C7]/10">
                <div className="w-12 h-12 flex items-center justify-center bg-[#C4A5C7]/20 rounded-full mb-3">
                  <i className="ri-compass-3-line text-[#C4A5C7] text-2xl"></i>
                </div>
                <p className="text-base font-semibold text-gray-900 mb-1">Mapa de Visión</p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Herramienta única para sacar del subconsciente tus temas de verdad
                </p>
              </div>

              <div className="bg-white rounded-xl p-5 shadow-sm border border-[#C4A5C7]/10">
                <div className="w-12 h-12 flex items-center justify-center bg-[#C4A5C7]/20 rounded-full mb-3">
                  <i className="ri-book-3-line text-[#C4A5C7] text-2xl"></i>
                </div>
                <p className="text-base font-semibold text-gray-900 mb-1">Diario de Integración</p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Guía para convertir tu visión en un tema claro con ángulo propio
                </p>
              </div>

              <div className="bg-white rounded-xl p-5 shadow-sm border border-[#C4A5C7]/10">
                <div className="w-12 h-12 flex items-center justify-center bg-[#C4A5C7]/20 rounded-full mb-3">
                  <i className="ri-edit-line text-[#C4A5C7] text-2xl"></i>
                </div>
                <p className="text-base font-semibold text-gray-900 mb-1">Ejercicios + Cierre</p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Banco de ejercicios visionarios y ritual de cierre para encarnar tu voz
                </p>
              </div>
            </div>

            <div className="text-center">
              {loading ? (
                <div className="px-10 py-4 bg-gray-200 rounded-full text-gray-500 font-semibold inline-block">
                  Cargando...
                </div>
              ) : hasPurchased ? (
                <Link
                  to="/contenido/cielo"
                  className="group inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-[#C4A5C7] to-[#B394B6] text-white rounded-full text-lg font-semibold hover:shadow-xl transition-all whitespace-nowrap transform hover:scale-105"
                >
                  <span>Acceder al contenido</span>
                  <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
                </Link>
              ) : (
                <a
                  href="#contacto"
                  onClick={handlePurchase}
                  className="group inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-[#C4A5C7] to-[#B394B6] text-white rounded-full text-lg font-semibold hover:shadow-xl transition-all whitespace-nowrap transform hover:scale-105"
                >
                  <span>Quiero empezar mi ritual Cielo</span>
                  <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-6 bg-[#F9F5FA]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-serif text-gray-900 mb-4">Por qué funciona</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#C4A5C7] to-[#B394B6] mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="group bg-white rounded-3xl p-10 shadow-lg border border-gray-100 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#C4A5C7]/30 to-[#C4A5C7]/10 rounded-2xl mb-8 group-hover:scale-110 transition-transform duration-500">
                <i className="ri-eye-line text-4xl text-[#B394B6]"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-serif text-gray-900 mb-4 leading-tight">
                Ve más allá de lo <span className="text-[#B394B6]">inmediato</span>
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                No escribas solo lo que ya sabes. Este ritual te conecta con las intuiciones y
                preguntas que <strong className="text-gray-800">tu mente consciente suele ignorar</strong>.
              </p>
            </div>

            <div className="group bg-white rounded-3xl p-10 shadow-lg border border-gray-100 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#C4A5C7]/30 to-[#C4A5C7]/10 rounded-2xl mb-8 group-hover:scale-110 transition-transform duration-500">
                <i className="ri-lightbulb-flash-line text-4xl text-[#B394B6]"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-serif text-gray-900 mb-4 leading-tight">
                Encuentra tu <span className="text-[#B394B6]">"por qué" creativo</span>
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Descubre tus obsesiones, preguntas e ideas‑faro. Los temas que{' '}
                <strong className="text-gray-800">vuelven una y otra vez</strong> porque son tuyos.
              </p>
            </div>

            <div className="group bg-white rounded-3xl p-10 shadow-lg border border-gray-100 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#C4A5C7]/30 to-[#C4A5C7]/10 rounded-2xl mb-8 group-hover:scale-110 transition-transform duration-500">
                <i className="ri-global-line text-4xl text-[#B394B6]"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-serif text-gray-900 mb-4 leading-tight">
                Escribe sobre el mundo <span className="text-[#B394B6]">sin caer en el ruido</span>
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Una experiencia profunda pero accesible.{' '}
                <strong className="text-gray-800">No necesitas experiencia previa</strong> en
                meditación contemplativa.
              </p>
            </div>

            <div className="group bg-white rounded-3xl p-10 shadow-lg border border-gray-100 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#C4A5C7]/30 to-[#C4A5C7]/10 rounded-2xl mb-8 group-hover:scale-110 transition-transform duration-500">
                <i className="ri-infinity-line text-4xl text-[#B394B6]"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-serif text-gray-900 mb-4 leading-tight">
                Cada entrada es <span className="text-[#B394B6]">diferente</span>
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Es tuyo para siempre. Cada vez que lo uses,{' '}
                <strong className="text-gray-800">
                  la pregunta o el foco que elijas cambiará la experiencia
                </strong>
                , revelando nuevas capas de tu visión creativa.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Preview Section */}
      <section className="py-20 px-6 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-72 h-72 bg-[#C4A5C7]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-72 h-72 bg-[#C4A5C7]/5 rounded-full blur-3xl"></div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-[#C4A5C7]/10 rounded-full">
              <i className="ri-eye-line text-[#C4A5C7]"></i>
              <span className="text-[#C4A5C7] text-sm font-semibold">
                DESCUBRE LO QUE INCLUYE
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4">
              Un vistazo al ritual
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Escucha un fragmento de la apertura de visión y conoce los materiales que te
              acompañarán
            </p>
          </div>

          {/* Audio Preview */}
          <div className="max-w-2xl mx-auto mb-12">
            <div className="bg-gradient-to-br from-[#F9F5FA] to-white rounded-3xl p-8 border border-gray-100 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-[#C4A5C7] to-[#B394B6] rounded-xl shadow-md">
                  <i className="ri-headphone-line text-white text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-serif text-gray-900">
                    Apertura de visión guiada
                  </h3>
                  <p className="text-sm text-gray-500">Fragmento de 20 segundos</p>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm mb-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#C4A5C7]/20 to-[#C4A5C7]/5 rounded-full">
                    <i className="ri-music-2-line text-[#B394B6] text-3xl"></i>
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 mb-1">
                      Ritual Cielo - Visión 432 Hz
                    </p>
                    <p className="text-sm text-gray-500">Amplía tu mirada creativa</p>
                  </div>
                </div>

                <audio
                  controls
                  className="w-full h-12 rounded-lg"
                  style={{ accentColor: '#B394B6' }}
                >
                  <source
                    src="https://static.readdy.ai/audio/vision-preview-20s.mp3"
                    type="audio/mpeg"
                  />
                  Tu navegador no soporta el elemento de audio.
                </audio>

                <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-timer-line"></i> Vista previa: 0:20
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i> Duración completa: 30 min
                  </span>
                </div>
              </div>

              <p className="text-sm text-gray-600 italic text-center">
                "Imagina que tu mirada se expande... más allá de lo inmediato, hacia el horizonte de
                lo posible..."
              </p>
            </div>
          </div>

          {/* Materials Preview */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="group bg-gradient-to-br from-[#F9F5FA] to-white rounded-3xl p-6 border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-500">
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-5">
                  <div className="w-40 h-52 rounded-xl overflow-hidden shadow-lg transform group-hover:scale-105 transition-transform duration-500">
                    <img
                      src="https://readdy.ai/api/search-image?query=Elegant%20intention%20cards%20deck%20with%20soft%20lavender%20edges%20and%20cosmic%20star%20illustrations%20on%20cream%20paper%20featuring%20moon%20phases%20and%20celestial%20symbols%20minimalist%20visionary%20design%20warm%20muted%20purple%20tones%20product%20photography%20on%20marble%20surface&width=300&height=400&seq=tarjetas-cielo-mockup&orientation=portrait"
                      alt="Tarjetas de intención"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-10 h-10 flex items-center justify-center bg-[#C4A5C7] rounded-full shadow-md">
                    <i className="ri-bookmark-line text-white text-lg"></i>
                  </div>
                </div>
                <h3 className="text-xl font-serif text-gray-900 mb-2">Tarjetas de intención</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  Elige desde dónde vas a mirar: una pregunta, una preocupación, una intuición.
                </p>
                <span className="inline-flex items-center gap-1 text-xs text-[#C4A5C7] font-medium bg-[#C4A5C7]/10 px-3 py-1 rounded-full">
                  <i className="ri-file-download-line"></i> PDF descargable
                </span>
              </div>
            </div>

            <div className="group bg-gradient-to-br from-[#F9F5FA] to-white rounded-3xl p-6 border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-500">
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-5">
                  <div className="w-40 h-52 rounded-xl overflow-hidden shadow-lg transform group-hover:scale-105 transition-transform duration-500">
                    <img
                      src="https://readdy.ai/api/search-image?query=Beautiful%20vision%20map%20poster%20with%20cosmic%20elements%20stars%20questions%20and%20intuitions%20flowing%20lines%20on%20aged%20parchment%20paper%20soft%20lavender%20tones%20artistic%20stationery%20product%20photography%20on%20wooden%20desk%20with%20crystals&width=300&height=400&seq=mapa-vision-mockup&orientation=portrait"
                      alt="Mapa de visión"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-10 h-10 flex items-center justify-center bg-[#C4A5C7] rounded-full shadow-md">
                    <i className="ri-compass-3-line text-white text-lg"></i>
                  </div>
                </div>
                <h3 className="text-xl font-serif text-gray-900 mb-2">Mapa de visión</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  Saca del subconsciente tus temas de verdad: preocupaciones, preguntas, intuiciones.
                </p>
                <span className="inline-flex items-center gap-1 text-xs text-[#C4A5C7] font-medium bg-[#C4A5C7]/10 px-3 py-1 rounded-full">
                  <i className="ri-palette-line"></i> Ilustrado a mano
                </span>
              </div>
            </div>

            <div className="group bg-gradient-to-br from-[#F9F5FA] to-white rounded-3xl p-6 border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-500">
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-5">
                  <div className="w-40 h-52 rounded-xl overflow-hidden shadow-lg transform group-hover:scale-105 transition-transform duration-500">
                    <img
                      src="https://readdy.ai/api/search-image?query=Beautiful%20integration%20journal%20with%20soft%20lavender%20cover%20and%20visionary%20cosmic%20illustrations%20handwritten%20calligraphy%20title%20elegant%20minimalist%20design%20warm%20muted%20purple%20tones%20product%20photography%20on%20wooden%20desk%20with%20amethyst%20crystals&width=300&height=400&seq=diario-cielo-mockup&orientation=portrait"
                      alt="Diario de integración"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-10 h-10 flex items-center justify-center bg-[#C4A5C7] rounded-full shadow-md">
                    <i className="ri-book-3-line text-white text-lg"></i>
                  </div>
                </div>
                <h3 className="text-xl font-serif text-gray-900 mb-2">Diario de integración</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  Convierte tu visión en un tema claro y elige un ángulo propio.
                </p>
                <span className="inline-flex items-center gap-1 text-xs text-[#C4A5C7] font-medium bg-[#C4A5C7]/10 px-3 py-1 rounded-full">
                  <i className="ri-file-download-line"></i> +20 páginas guiadas
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="contacto" className="py-24 px-6 bg-gradient-to-b from-white to-[#F9F5FA]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-6">
            Empieza tu ritual Cielo hoy
          </h2>
          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
            Amplía tu mirada y ve más allá de lo inmediato. Expande tu voz hacia nuevos horizontes
            creativos
          </p>

          <div className="bg-white rounded-3xl p-10 shadow-xl border border-gray-100 mb-10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="text-left">
                <div className="text-5xl font-bold text-[#C4A5C7] mb-2">64€</div>
                <p className="text-gray-600">Pago único • Acceso inmediato</p>
                <p className="text-sm text-gray-500 mt-2">Acceso ilimitado para siempre</p>
              </div>

              {loading ? (
                <div className="px-10 py-4 bg-gray-200 rounded-full text-gray-500 font-semibold">
                  Cargando...
                </div>
              ) : hasPurchased ? (
                <Link
                  to="/contenido/cielo"
                  className="group inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-[#C4A5C7] to-[#B394B6] text-white rounded-full text-lg font-semibold hover:shadow-xl transition-all whitespace-nowrap transform hover:scale-105"
                >
                  <span>Acceder al contenido</span>
                  <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
                </Link>
              ) : (
                <a
                  href="mailto:contacto@unavozmasquepropia.com?subject=Quiero%20comprar%20Ritual%20Cielo&body=Hola,%20estoy%20interesada%20en%20comprar%20el%20Ritual%20Cielo%20por%2064€."
                  className="group inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-[#C4A5C7] to-[#B394B6] text-white rounded-full text-lg font-semibold hover:shadow-xl transition-all whitespace-nowrap transform hover:scale-105"
                >
                  <span>Empezar mi ritual Cielo</span>
                  <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
                </a>
              )}
            </div>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-6 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <i className="ri-shield-check-line text-[#C4A5C7] text-xl"></i>
              <span>Pago seguro</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-customer-service-line text-[#C4A5C7] text-xl"></i>
              <span>Soporte personalizado</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-heart-line text-[#C4A5C7] text-xl"></i>
              <span>Hecho con amor</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#283748] text-white py-12 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <Link to="/" className="inline-block mb-6">
            <img
              src="https://static.readdy.ai/image/e4930f1328430401d33e8e8f8ac29155/d3c9f2d28e8d17a63b0e1d543ffef045.png"
              alt="Una voz más que propia"
              className="h-16 w-auto mx-auto"
            />
          </Link>
          <p className="text-white/70 mb-6">
            Encuentra tu voz única a través de la meditación y la escritura creativa
          </p>
          <div className="flex justify-center gap-6 mb-6">
            <a
              href="mailto:contacto@unavozmasquepropia.com"
              className="text-white/70 hover:text-white transition-colors"
            >
              <i className="ri-mail-line text-2xl"></i>
            </a>
          </div>
          <div className="border-t border-white/10 pt-6">
            <p className="text-white/50 text-sm">
              © 2026 Una voz más que propia. Todos los derechos reservados. •{' '}
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors"
              >
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
